
import React from "react";
import { Button } from "@/components/ui/button";
import { SectionHeader } from "@/components/ui/section-header";
import { AlertTriangle, RefreshCw, ArrowLeft, Database } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";

interface ErrorStateProps {
  countyName: string;
  error: Error;
}

export function ErrorState({ countyName, error }: ErrorStateProps) {
  const handleRefresh = () => {
    window.location.reload();
  };

  // Determine if this is likely a database connection error or empty data issue
  const isDatabaseError = error.message.includes("database") || 
                          error.message.includes("supabase") ||
                          error.message.includes("table") ||
                          error.message.includes("query");
  
  const isEmptyDataError = error.message.includes("no rows") ||
                           error.message.includes("not found") ||
                           error.message.includes("empty");

  return (
    <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
      <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
      <SectionHeader 
        title="No Nursing Homes Found" 
        subtitle={`We couldn't find any nursing homes for ${countyName} county in our database.`}
        centered
      />
      
      <div className="mx-auto max-w-xl my-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-center mb-3">
              <Database className="h-5 w-5 text-slate-600 mr-2" />
              <h3 className="text-lg font-medium">Database Information</h3>
            </div>
            
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Parameter</TableHead>
                  <TableHead>Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell>County</TableCell>
                  <TableCell>{countyName}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>County Slug</TableCell>
                  <TableCell>{countyName.toLowerCase()}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Query Status</TableCell>
                  <TableCell className="text-red-500">Failed</TableCell>
                </TableRow>
              </TableBody>
            </Table>
            
            <div className="mt-4 bg-slate-50 p-3 rounded-md border border-slate-200">
              <p className="text-sm text-red-500 font-mono break-all overflow-x-auto">
                {error.message || "Unknown error occurred"}
              </p>
            </div>
            
            <div className="mt-4 bg-amber-50 p-3 rounded-md border border-amber-200">
              <p className="text-amber-700 text-sm">
                {isDatabaseError ? (
                  <>
                    This appears to be a database issue. The nursing homes database might not have 
                    entries for {countyName} yet, or there might be a connection issue.
                  </>
                ) : isEmptyDataError ? (
                  <>
                    We don't have any nursing homes listed for {countyName} in our database yet.
                    Our team is actively adding more locations. Please check back later.
                  </>
                ) : (
                  <>
                    An unexpected error occurred while trying to load nursing homes for {countyName}.
                    Please try again later.
                  </>
                )}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <div className="flex flex-col sm:flex-row justify-center gap-4 mt-6">
        <Button 
          onClick={handleRefresh}
          variant="default"
          className="flex items-center"
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Try Again
        </Button>
        
        <Button 
          onClick={() => window.location.href = "/"}
          variant="outline"
          className="flex items-center"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Return to Home
        </Button>
      </div>
    </div>
  );
}
